// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { Modal } from '../../components/Modal';
import { createTheme } from '../../design-system/theme';
import { activityStrings } from './strings';
import type { ActivitySort } from './types';

export interface ActivitySortSheetProps {
  readonly visible: boolean;
  readonly active: ActivitySort;
  readonly onSelect: (sort: ActivitySort) => void;
  readonly onClose: () => void;
}

const SORT_OPTIONS: readonly ActivitySort[] = ['mostRecent', 'byPerson', 'byChannel'];

export const ActivitySortSheet: React.FC<ActivitySortSheetProps> = ({
  visible,
  active,
  onSelect,
  onClose,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  const handleSelect = useCallback(
    (sort: ActivitySort) => {
      onSelect(sort);
      onClose();
    },
    [onSelect, onClose],
  );

  return (
    <Modal visible={visible} onClose={onClose} accessibilityLabel={activityStrings.sortLabel}>
      <View style={styles.container}>
        <Text style={[styles.title, { color: theme.colors.text.primary }]}>
          {activityStrings.sortLabel}
        </Text>
        {SORT_OPTIONS.map((sort) => {
          const selected = sort === active;
          return (
            <Pressable
              key={sort}
              onPress={() => handleSelect(sort)}
              accessibilityRole="button"
              accessibilityLabel={activityStrings.sort(sort)}
              accessibilityState={{ selected }}
              style={({ pressed }) => [
                styles.row,
                {
                  backgroundColor: selected ? `${theme.colors.accent.indigo}1A` : 'transparent',
                  opacity: pressed ? 0.6 : 1,
                },
              ]}
            >
              <Text
                style={[
                  styles.rowText,
                  {
                    color: selected ? theme.colors.accent.indigo : theme.colors.text.primary,
                    fontWeight: selected ? '700' : '500',
                  },
                ]}
              >
                {activityStrings.sort(sort)}
              </Text>
              {selected && (
                <Text style={[styles.checkmark, { color: theme.colors.accent.indigo }]}>✓</Text>
              )}
            </Pressable>
          );
        })}
      </View>
    </Modal>
  );
};

/** Convenience export so tests can iterate the same source of truth. */
export const ACTIVITY_SORT_OPTIONS = SORT_OPTIONS;

const styles = StyleSheet.create({
  container: {
    width: '100%',
    paddingVertical: 8,
  } satisfies ViewStyle,
  title: {
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
    paddingHorizontal: 4,
  } satisfies TextStyle,
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderRadius: 8,
  } satisfies ViewStyle,
  rowText: {
    fontSize: 15,
    flex: 1,
  } satisfies TextStyle,
  checkmark: {
    fontSize: 16,
    fontWeight: '700',
  } satisfies TextStyle,
});
